import { useTranslation } from "@i18n";
import { useInView } from "react-intersection-observer";
import SelectDropDown from "@CubeComponents/Mui_Form/SelectDropDown";
import type { DropdownItem } from "@Helpers/types/layout/types";
import type { LayoutData } from "@ServerTypes/layoutView/layoutView.schema";
import type { SelectEventArgs } from "@CubeComponents/Mui_Form/SelectDropDown";
import horizontalThreeDots from "../../assets/icons/svg/HorizontalThreeDots.svg";
import "./viewCss.css";
import TextFieldComponent from "@CubeComponents/Mui_Form/TextFieldComponent";
import UserNameTagComponent from "../../cubeComponent/html/UserNameTagComponent";
import IconComponent from "@CubeComponents/icons";
import { ButtonBase } from "@mui/material";
import _ from "lodash";

/** One row of the layout list: the saved view document plus the position `Views` stamps on it. */
export type LayoutItem = LayoutData & { index?: number };

/**
 * `SelectDropDown`'s `items` prop is `ActionMenuItem[]`, which carries a `[key: string]: unknown`
 * index signature. A named interface such as `DropdownItem` never gets an implicit index signature,
 * so it is not assignable to it; the same shape written as a mapped type does. Callers can still pass
 * a plain `DropdownItem[]`, because `DropdownItem` is assignable to this alias.
 */
type MenuDropdownItem = { [K in keyof DropdownItem]: DropdownItem[K] };

interface Props {
  item: LayoutItem;
  /** The row currently being renamed, or `{}` when none is. */
  renameData: Partial<LayoutData>;
  currentDataId?: string;
  dropDownItems: MenuDropdownItem[];
  onClickLayout: (item: LayoutItem) => void;
  handleSelectedOption: (args: { args: SelectEventArgs; layoutData: LayoutItem; layoutDataIndex: number }) => void;
  setRenameData: (data: Partial<LayoutData>) => void;
  onTickClick: () => void;
}

const LayoutRow = ({
  item,
  renameData,
  currentDataId,
  dropDownItems,
  onClickLayout,
  handleSelectedOption,
  setRenameData,
  onTickClick,
}: Props) => {
  const { t } = useTranslation();
  const { ref, inView } = useInView({
    threshold: 0.1,
    rootMargin: "50px",
    triggerOnce: false,
  });

  return (
    <div ref={ref}>
      {renameData._id == item._id ? (
        <div className="flex flex-row items-center">
          <TextFieldComponent
            type="text"
            value={renameData.name}
            onChange={(args) => {
              setRenameData({ ...renameData, name: args.target.value });
            }}
          />
          <IconComponent className="mx-2" name="cross" width="1rem" height="1rem" onClick={() => setRenameData({})} />
          <IconComponent name="tick2" width="1rem" height="1rem" onClick={onTickClick} />
        </div>
      ) : (
        <ButtonBase
          className="group flex text-start items-center w-full flex-row gap-2 h-10 px-2 hover:bg-primary_blue"
          onClick={() => onClickLayout(item)}
        >
          <div className="flex min-w-0 flex-1 flex-row items-center gap-2">
            <div className="text-sm truncate">{item.name}</div>
            {item.isDefault && (
              <div
                className="flex items-center gap-1 h-6 px-2 py-1 rounded-lg bg-gray-500 text-white text-center text-xs font-semibold leading-[1.2] flex-none"
                data-testid="defaultLayout"
              >
                {t("Default")}
              </div>
            )}
            {currentDataId === item._id && (
              <div
                className="flex items-center gap-1 h-6 px-2 py-1 rounded-lg bg-gradient_color text-white text-center text-xs font-semibold leading-[1.2] flex-none"
                data-testid="currentLayout"
              >
                {t("Current")}
              </div>
            )}
          </div>
          {/* Owner tag + row menu stay pinned to the trailing edge. */}
          <div className="flex flex-none flex-row gap-2 items-center">
            {item.isShared && inView && <UserNameTagComponent id={_.get(item, "createdByData._id")} />}
            <div className="flex-none w-8 items-center">
              <div className="group-hover:block">
                {/* Views.layoutContent decides what a row may do; empty means no menu. */}
                {dropDownItems.length > 0 && (
                  <div
                    onClick={(e: React.MouseEvent) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                  >
                    <SelectDropDown<MenuDropdownItem>
                      style={{ height: "0.625rem" }}
                      select={(args: SelectEventArgs) => {
                        handleSelectedOption({
                          args,
                          layoutData: item,
                          // `index` is stamped onto every row by Views.layoutContent before render.
                          layoutDataIndex: item.index!,
                        });
                      }}
                      items={dropDownItems}
                      renderOption={(option) => (
                        <span className="flex items-center gap-2">
                          {option.iconName ? (
                            <IconComponent name={option.iconName} width="1.25rem" height="1.25rem" />
                          ) : null}
                          {option.text}
                        </span>
                      )}
                      cssClass="e-caret-hide p-0 bg-white border-none preventLayoutClose group-hover:bg-primary_blue"
                      onClick={(e: React.MouseEvent) => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                    >
                      <img
                        alt="options"
                        src={horizontalThreeDots}
                        style={{
                          top: "-0.4375rem",
                          display: "block",
                          position: "relative",
                        }}
                      />
                    </SelectDropDown>
                  </div>
                )}
              </div>
            </div>
          </div>
        </ButtonBase>
      )}
    </div>
  );
};

export default LayoutRow;
